// Generic idle prefetch helper for lazily loaded feature chunks.
// Uses requestIdleCallback when available, otherwise falls back to setTimeout.
// Each loader should be a function that triggers a dynamic import (e.g.,
// () => import('...'))

type PrefetchTask = () => Promise<unknown>;

interface IdlePrefetchOptions {
  timeout?: number; // ms before prefetch starts if idle callback not fired
  concurrency?: number; // how many imports to run in parallel
}

// minimal typing for requestIdleCallback for TS projects without the lib.dom experimental type
const schedule = (cb: () => void, timeout?: number) => {
  const ric: any = (typeof window !== 'undefined' && (window as any).requestIdleCallback) || null;
  if (ric) {
    return ric(() => cb(), { timeout: timeout ?? 3000 });
  }
  return setTimeout(cb, timeout ?? 250);
};

/**
 * Prefetch (warm) a set of dynamic import tasks during browser idle time.
 * Ensures we don't compete with critical render work.
 */
export const idlePrefetch = (tasks: PrefetchTask[], options?: IdlePrefetchOptions) => {
  if (!Array.isArray(tasks) || tasks.length === 0) return;
  const concurrency = options?.concurrency ?? 1;
  let inFlight = 0;
  let index = 0;

  const runNext = () => {
    while (inFlight < concurrency && index < tasks.length) {
      const task = tasks[index++];
      inFlight++;
      task()
        .catch(() => {
          // swallow errors; prefetch is opportunistic
        })
        .finally(() => {
          inFlight--;
          runNext();
        });
    }
  };

  schedule(runNext, options?.timeout);
};

// Hook variant for React components
import { useEffect } from 'react';

export const useIdlePrefetch = (tasks: PrefetchTask[] | undefined, options?: IdlePrefetchOptions) => {
  useEffect(() => {
    if (!tasks || tasks.length === 0) return;
    idlePrefetch(tasks, options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
